import AppConsts, { AppConfiguration } from "./../lib/appconst"
import { LError } from "@lib/abpUtility"
import { notifyError } from "@lib/helper"
import { Modal } from "antd"
import axios, { AxiosRequestConfig } from "axios"
import { uiProgressStoreInstance } from "@stores/ui/uiProgressStore"
const { authorization } = AppConsts
import qs from "qs"

// const qs = require("qs")
let isBlockedModalShown = false
declare let abp: any

// Per-request axios config understood by this service's interceptors.
// skipErrorNotification: true → the caller renders the error itself (e.g. the
// auth pages' inline banner) and the global notifyError toast is suppressed.
// skipProgressBar: true → request chạy ngầm (polling định kỳ), không kéo thanh
// progress ở header. Xem `beginProgress` bên dưới.
export type ApiRequestConfig = AxiosRequestConfig & {
  skipErrorNotification?: boolean
  skipProgressBar?: boolean
}

/** Hạn mặc định cho API thường. */
const DEFAULT_TIMEOUT_MS = 30000

/**
 * Hạn riêng cho export/import: server phải dựng file Excel (đọc dữ liệu + ghi template) nên 30s là
 * quá chật — báo cáo lớn (vd `Export/ExportReportDebitNote`) bị axios huỷ giữa chừng trong khi
 * server vẫn đang chạy, người dùng thấy "Network Error" chứ không phải lỗi thật.
 */
const LONG_RUNNING_TIMEOUT_MS = 60000

/**
 * Khớp cả "api/Export/..." lẫn "/api/Export/..." — hai dạng đều đang được dùng trong services.
 *
 * `DownloadRequest` nằm trong danh sách này vì nhánh ĐỒNG BỘ của Download Request Center dựng file
 * Excel ngay trong `CreateRequest` (và `GetPreview` chạy trọn câu truy vấn báo cáo để đếm) — cùng
 * loại công việc mà `api/Export/...` được nâng hạn vì 30s là quá chật. Bỏ sót ở đây là lặp lại đúng
 * lỗi cũ trên một route mới: axios cắt giữa chừng trong khi server vẫn đang ghi file.
 */
const LONG_RUNNING_URL_PATTERN =
  /(^|\/)api\/(Export|Imports)\/|(^|\/)api\/(services\/app\/)?DownloadRequest\//i

/**
 * Cờ đánh dấu request này ĐÃ gọi `start()`, gắn thẳng lên config để `endProgress` bảo đảm mỗi
 * request chỉ `done()` đúng một lần. Refcount trong store lệch một nhịp là thanh treo vĩnh viễn
 * (thiếu done) hoặc tắt sớm khi vẫn còn request đang chạy (thừa done).
 */
const PROGRESS_STARTED_KEY = "__uiProgressStarted"

/** Bắt đầu đếm một tác vụ cho thanh progress, trừ khi caller xin miễn (`skipProgressBar`). */
const beginProgress = (config: any) => {
  if (!config || config.skipProgressBar || config[PROGRESS_STARTED_KEY]) return
  config[PROGRESS_STARTED_KEY] = true
  uiProgressStoreInstance.start()
}

/** Kết thúc tác vụ tương ứng. Idempotent: gọi lại trên cùng config sẽ không trừ thêm lần nữa. */
const endProgress = (config: any) => {
  if (!config || !config[PROGRESS_STARTED_KEY]) return
  config[PROGRESS_STARTED_KEY] = false
  uiProgressStoreInstance.done()
}

const http = axios.create({
  baseURL: AppConfiguration.remoteServiceBaseUrl,
  timeout: DEFAULT_TIMEOUT_MS,
  paramsSerializer: function (params) {
    return qs.stringify(params, {
      encode: true,
    })
  },
})

http.interceptors.request.use(
  function (config) {
    // Đặt ở interceptor chung thay vì từng màn: trước đây chỉ vài trang tự gọi `track()` nên phần
    // lớn thao tác chờ API không có phản hồi thị giác nào.
    beginProgress(config)

    // Request bị huỷ (abort/cancel) nổi lên interceptor lỗi dưới dạng đối tượng `Cancel` KHÔNG kèm
    // `config`, nên nhánh đó không biết phải nhả refcount nào — thanh sẽ treo vĩnh viễn. Bám thẳng
    // vào tín hiệu huỷ để tự nhả; `endProgress` idempotent nên nhả hai lần cũng vô hại.
    const signal: any = (config as any).signal
    signal?.addEventListener?.("abort", () => endProgress(config), { once: true })
    ;(config as any).cancelToken?.promise?.then(() => endProgress(config))

    // Chỉ nâng những request còn ở mức MẶC ĐỊNH. Điều kiện phải so với `DEFAULT_TIMEOUT_MS` chứ
    // không phải "< LONG_RUNNING_TIMEOUT_MS": axios đã merge hạn mặc định của instance vào config
    // trước khi interceptor chạy, nên mọi hạn caller tự đặt — kể cả hạn THẤP hơn có chủ ý, ví dụ
    // `getMySummary` đặt 10s cho polling nền — đều nhỏ hơn 60s và sẽ bị nâng oan.
    const callerTimeout = config.timeout
    if (
      LONG_RUNNING_URL_PATTERN.test(config.url || "") &&
      (!callerTimeout || callerTimeout === DEFAULT_TIMEOUT_MS)
    ) {
      config.timeout = LONG_RUNNING_TIMEOUT_MS
    }

    if (!config.headers) {
      config.headers = {}
    }
    if (abp.auth.getToken()) {
      config.headers.common["Authorization"] = "Bearer " + abp.auth.getToken()
    }

    config.headers.common[".AspNetCore.Culture"] = abp.utils.getCookieValue(
      "Abp.Localization.CultureName"
    )
    config.headers.common["Abp.TenantId"] = abp.multiTenancy.getTenantIdCookie()
    config.headers.common["TargetApplication"] =
      authorization.targetApplication || 1
    config.params = {
      ...(config.params || {}),
      culture: abp.utils.getCookieValue("Abp.Localization.CultureName") || "vi",
    }
    //

    return config
  },
  function (error) {
    endProgress(error?.config)
    return Promise.reject(error)
  }
)

http.interceptors.response.use(
  (response) => {
    endProgress(response?.config)
    return response
  },
  (res) => {
    // Trả thanh progress TRƯỚC mọi nhánh xử lý lỗi bên dưới: nhánh 406 `return` sớm (không reject)
    // nên nếu đặt cuối hàm thì request bị khoá tài khoản sẽ không bao giờ nhả refcount.
    endProgress(res?.config)

    // status code 406: account is blocked

    if (res?.response?.status === 406) {
      if (!isBlockedModalShown) {
        isBlockedModalShown = true
        Modal.error({
          title: "Tài khoản đã bị khoá/ Your account has been locked.",
          content:
            "Tài khoản đã bị khóa. Bạn không thể tiếp tục đăng nhập. Liên hệ bộ phận hỗ trợ để biết thêm chi tiết.\nYour account has been locked. Login is no longer possible. Please contact our support team for further assistance.",

          onOk: () => {
            abp.utils.deleteCookie(
              AppConsts.authorization.encrptedAuthTokenName,
              abp.appPath
            )
            abp.utils.deleteCookie(
              AppConsts.authorization.projectId,
              abp.appPath
            )
            localStorage.clear()
            sessionStorage.clear()
            abp.auth.clearToken()
            window.location.href = "/logout"
          },
        })
      }
      return
    }
    if (res?.response?.status === 401) {
      abp.utils.deleteCookie(
        AppConsts.authorization.encrptedAuthTokenName,
        abp.appPath
      )
      abp.utils.deleteCookie(AppConsts.authorization.projectId, abp.appPath)

      localStorage.clear()
      sessionStorage.clear()
      abp.auth.clearToken()

      window.location.href = "/logout"
    }
    // Kiểm tra flag skipErrorNotification để bỏ qua notification popup
    const skipErrorNotification = (res?.config as any)?.skipErrorNotification
    const notifyAbpError = (error: any) => {
      if (skipErrorNotification) return
      if (!!error.message && error.details) {
        notifyError(error.details, error.message)
      } else if (error.message) {
        notifyError(LError("REQUEST_ERROR"), error.message)
      }
    }

    // Request export/download đi với `responseType: "blob"` nên body LỖI (JSON envelope của ABP)
    // cũng về dưới dạng Blob: destructure `.error` ra `undefined` ⇒ trước đây user không thấy thông
    // báo nào, chỉ có một request thất bại im lặng. Đọc blob ra text rồi đi lại đúng nhánh thông báo.
    const responseData = res?.response?.data
    if (responseData instanceof Blob) {
      return responseData.text().then(
        (text) => {
          let blobError: any
          try {
            blobError = JSON.parse(text)?.error
          } catch {
            blobError = undefined
          }
          if (blobError) notifyAbpError(blobError)
          return Promise.reject(blobError)
        },
        () => Promise.reject(undefined)
      )
    }

    const { error } = responseData || {}
    if (!error) {
      // notifyError(LError("UNKNOW_ERROR"), "");
      return Promise.reject(error)
    }

    notifyAbpError(error)

    return Promise.reject(error)
  }
)

export const customAxiosInstance = <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  return http(config) as Promise<T>
}

export default http
